import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Card, CardContent } from './ui/card';
import { Slider } from './ui/slider';
import { Volume2, VolumeX, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU';

/**
 * NotificationSoundSettings - Toggle for notification sounds
 * Easy-to-access toggle for customers to turn off sounds
 */
export function NotificationSoundToggle({ className = '' }) {
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('notificationSoundEnabled');
    return stored !== 'false'; // Default to true
  });
  
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('notificationSoundEnabled', String(newValue));
    
    if (newValue) {
      // Play test sound
      playNotificationSound();
      toast.success('Notification sounds enabled');
    } else {
      toast.success('Notification sounds disabled');
    }
  };
  
  return (
    <button
      onClick={toggleSound}
      className={`p-2 rounded-full transition-colors ${
        soundEnabled 
          ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
      } ${className}`}
      title={soundEnabled ? 'Sound on - tap to mute' : 'Sound off - tap to unmute'}
      data-testid="notification-sound-toggle"
    >
      {soundEnabled ? (
        <Volume2 className="w-5 h-5" />
      ) : (
        <VolumeX className="w-5 h-5" />
      )}
    </button>
  );
}

/**
 * Full Notification Settings Panel
 */
export function NotificationSettings({ onClose }) {
  const { user, getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState({
    soundEnabled: localStorage.getItem('notificationSoundEnabled') !== 'false',
    soundVolume: parseInt(localStorage.getItem('notificationVolume') || '70'),
    pushEnabled: localStorage.getItem('pushNotificationsEnabled') !== 'false',
    trackingUpdates: localStorage.getItem('trackingUpdatesSound') !== 'false',
    jobAlerts: localStorage.getItem('jobAlertsSound') !== 'false',
    messages: localStorage.getItem('messagesSound') !== 'false'
  });
  
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    // Save to localStorage
    if (key === 'soundEnabled') {
      localStorage.setItem('notificationSoundEnabled', String(value));
    } else if (key === 'soundVolume') {
      localStorage.setItem('notificationVolume', String(value));
    } else if (key === 'pushEnabled') {
      localStorage.setItem('pushNotificationsEnabled', String(value));
    } else if (key === 'trackingUpdates') {
      localStorage.setItem('trackingUpdatesSound', String(value));
    } else if (key === 'jobAlerts') {
      localStorage.setItem('jobAlertsSound', String(value));
    } else if (key === 'messages') {
      localStorage.setItem('messagesSound', String(value));
    }
  };
  
  const testSound = () => {
    playNotificationSound(settings.soundVolume / 100);
  };
  
  return (
    <Card className="w-full max-w-md" data-testid="notification-settings">
      <CardContent className="p-4 sm:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Notification Settings</h3>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              Done
            </Button>
          )}
        </div>
        
        {/* Master Sound Toggle */}
        <div className="flex items-center justify-between py-3 border-b">
          <div className="flex items-center gap-3">
            {settings.soundEnabled ? (
              <Volume2 className="w-5 h-5 text-blue-600" />
            ) : (
              <VolumeX className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <p className="font-medium">Notification Sounds</p>
              <p className="text-sm text-gray-500">Play sounds for updates</p>
            </div>
          </div>
          <Switch
            checked={settings.soundEnabled}
            onCheckedChange={(v) => handleSettingChange('soundEnabled', v)}
            data-testid="master-sound-toggle"
          />
        </div>
        
        {/* Volume Slider */}
        {settings.soundEnabled && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Volume</span>
              <span className="text-sm text-gray-500">{settings.soundVolume}%</span>
            </div>
            <div className="flex items-center gap-3">
              <VolumeX className="w-4 h-4 text-gray-400" />
              <Slider
                value={[settings.soundVolume]}
                onValueChange={([v]) => handleSettingChange('soundVolume', v)}
                max={100}
                step={10}
                className="flex-1"
              />
              <Volume2 className="w-4 h-4 text-gray-600" />
            </div>
            <Button variant="outline" size="sm" onClick={testSound} className="w-full">
              Test Sound
            </Button>
          </div>
        )}
        
        {/* Specific Sound Categories */}
        {settings.soundEnabled && (
          <div className="space-y-3 pt-3 border-t">
            <p className="text-sm font-medium text-gray-700">Sound Categories</p>
            
            <div className="flex items-center justify-between py-2">
              <span className="text-sm">Provider location updates</span>
              <Switch
                checked={settings.trackingUpdates}
                onCheckedChange={(v) => handleSettingChange('trackingUpdates', v)}
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <span className="text-sm">Job alerts (providers)</span>
              <Switch
                checked={settings.jobAlerts}
                onCheckedChange={(v) => handleSettingChange('jobAlerts', v)}
              />
            </div>
            
            <div className="flex items-center justify-between py-2">
              <span className="text-sm">New messages</span>
              <Switch
                checked={settings.messages}
                onCheckedChange={(v) => handleSettingChange('messages', v)}
              />
            </div>
          </div>
        )}
        
        {/* Push Notifications */}
        <div className="flex items-center justify-between py-3 border-t">
          <div className="flex items-center gap-3">
            {settings.pushEnabled ? (
              <Bell className="w-5 h-5 text-blue-600" />
            ) : (
              <BellOff className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <p className="font-medium">Push Notifications</p>
              <p className="text-sm text-gray-500">Background alerts</p>
            </div>
          </div>
          <Switch
            checked={settings.pushEnabled}
            onCheckedChange={(v) => handleSettingChange('pushEnabled', v)}
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Play notification sound
 */
export function playNotificationSound(volume = 0.7) {
  const enabled = localStorage.getItem('notificationSoundEnabled') !== 'false';
  if (!enabled) return;
  
  const storedVolume = parseInt(localStorage.getItem('notificationVolume') || '70') / 100;
  const finalVolume = volume || storedVolume;
  
  try {
    // Create a simple notification beep using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800; // Hz
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(finalVolume * 0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') console.log('Could not play notification sound:', e);
  }
}

/**
 * Check if sound is enabled for a specific category
 */
export function isSoundEnabledFor(category) {
  const masterEnabled = localStorage.getItem('notificationSoundEnabled') !== 'false';
  if (!masterEnabled) return false;
  
  switch (category) {
    case 'tracking':
      return localStorage.getItem('trackingUpdatesSound') !== 'false';
    case 'job':
      return localStorage.getItem('jobAlertsSound') !== 'false';
    case 'message':
      return localStorage.getItem('messagesSound') !== 'false';
    default:
      return masterEnabled;
  }
}

export default NotificationSettings;
